import React, {memo, useState} from 'react';
import Constants from '../../constants/Constants';
import {useTranslation} from 'react-i18next';
import {TextInput, ControlButton} from '@techsmith/tsc-cloud-style-guide';
import config from '../../service/config';
import SelfIdentifyBadge from '../../static/svg/SelfIdentifyBadge.svg';
import styled from 'styled-components';
import {StyledAnchor, StyledWarning} from '../util/StyledElements';
import '../../static/css/modal.less';
import selfIdentifyservice from '../../service/selfIdentifyservice';
import {useTscTheme} from '../../context/themeProvider';
import {BasicModalPortal} from '../util/ModalPortalHelper';

const FormBody = styled.div`
   justify-content: center;
   align-items: center;
   display: flex;
   flex-direction: column;
`;

const TitleWrapper = styled.div<{isEmbedded: boolean}>`
   font-weight: ${props => props.isEmbedded ? '600' : '400'};
   font-size: 1.2rem;
`;

const InlineAnchorWrapper = styled.div`
   a {
      padding-left: 0.25rem;
   }
`;

const InputWrapper = styled.div`
   width: 100%;
   display: flex;
   margin: 0.5rem 0;

   .self-identify-submit-button {
      margin-left: .25rem;
   }
`;

export const SelfIdentifyModalBase: React.FC<ISelfIdentifyModalProps> = ({showTosMessage, showSignInMessage, isEmbedded, onClose}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [guestName, setGuestName] = useState('');
   const [loadingState, setLoadingState] = useState({isWorking: false, hasError: false});

   const submit = async (): Promise<void> => {
      try {
         setLoadingState({isWorking: true, hasError: false});
         await selfIdentifyservice.setSelfIdentifiedUser(guestName);
         setLoadingState({isWorking: false, hasError: false});
         onClose();
      } catch {
         setLoadingState({isWorking: false, hasError: true});
      }
   };

   const inputErrorText = guestName.length > Constants.maxSelfIdentifyNameLength ? t('selfIdentify.invalidDisplayNameLength', {0: Constants.maxSelfIdentifyNameLength}) : '';

   return (
      <BasicModalPortal
         themeName={theme}
         width="26rem"
         onClose={onClose}
         headerVisible={false}
         testId="self-identify"
         isLoading={loadingState.isWorking}
      >
         <div className="self-identify modal-body">
            {!isEmbedded && <SelfIdentifyBadge />}
            <FormBody className="form-body">
               <TitleWrapper isEmbedded={isEmbedded}>
                  {t('selfIdentify.badgeText')}
               </TitleWrapper>
               {
                  showTosMessage && (
                     <InlineAnchorWrapper
                        data-test-id="self-identify-modal-tos-link"
                        className="self-identify-modal-tos-link"
                     >
                        {t('selfIdentify.tos')}
                        <StyledAnchor
                           href={config.featureSwitches.IsScreencast ? Constants.navigation.termsOfServiceScreencastLink : Constants.navigation.termsOfServiceLink}
                           target="_blank"
                        >
                           {t('selfIdentify.tosLink')}
                        </StyledAnchor>
                     </InlineAnchorWrapper>
                  )
               }
               {loadingState.hasError && (
                  <StyledWarning>
                     {t('general.errorText')}
                  </StyledWarning>
               )}
               <InputWrapper>
                  <TextInput
                     placeholder={t('selfIdentify.placeholder')}
                     errorText={inputErrorText}
                     value={guestName}
                     onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGuestName(e.target.value)}
                     onEscape={() => setGuestName('')}
                     onEnter={submit}
                     themeName={theme}
                     autoFocus={true}
                     ariaLabel={t('selfIdentify.badgeText')}
                     testId="self-identify-input"
                  />
                  <ControlButton
                     label={t('selfIdentify.buttonLabel')}
                     disabled={guestName === '' || !!inputErrorText}
                     themeName={theme}
                     onClick={submit}
                     className="self-identify-submit-button"
                     testId="self-identify-submit"
                  />
               </InputWrapper>
               {
                  showSignInMessage && (
                     <InlineAnchorWrapper
                        data-test-id="self-identify-modal-sign-in-link"
                        className="self-identify-modal-sign-in-link"
                     >
                        {t('selfIdentify.signin')}
                        <StyledAnchor href={config.environmentData.SignInUrl}>
                           {t('selfIdentify.signinLink')}
                        </StyledAnchor>
                     </InlineAnchorWrapper>
                  )
               }
            </FormBody>
         </div>
      </BasicModalPortal>
   );
};

export interface ISelfIdentifyModalProps {
   showTosMessage: boolean;
   showSignInMessage: boolean;
   isEmbedded: boolean;
   onClose(): void;
}

export default memo(SelfIdentifyModalBase);
